/**
 * Public privacy policy page. Linked from the toll-free SMS verification
 * form and from the platform footer. Plain prose; not legal advice.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-apple-bg font-sans antialiased">
      <header className="fixed inset-x-0 top-0 z-40 bg-apple-bg/80 backdrop-blur supports-[backdrop-filter]:bg-apple-bg/70">
        <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="text-sm font-semibold tracking-tight text-apple-ink">
            VentureOS
          </Link>
          <Link to="/" className="text-sm text-apple-subtext hover:text-apple-ink">
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 pt-24 pb-16 text-apple-ink">
        <h1 className="text-4xl font-semibold tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-apple-subtext mb-10">
          Effective: April 26, 2026 · Operated by VentureOS LLC
        </p>

        <section className="prose prose-sm max-w-none space-y-6 leading-relaxed text-[15px]">
          <h2 className="text-xl font-semibold mt-8">1. Who we are</h2>
          <p>
            VentureOS ("the Service") is a venture-capital deal-sourcing
            and intelligence platform operated by <strong>VentureOS LLC</strong>{' '}
            ("we", "us").
          </p>

          <h2 className="text-xl font-semibold mt-8">2. Information we collect</h2>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Account information</strong> — email address, name, and
              passwordless one-time passcodes used for login.
            </li>
            <li>
              <strong>Business contact information</strong> — investor and company
              records (names, firm names, titles, emails, phones, LinkedIn URLs,
              websites, public business records). Sources include direct entry,
              CSV/XLSX/vCard imports, business-card OCR, SMS capture from
              authorized internal staff, and public APIs (SEC EDGAR, Crunchbase
              free tier, Apollo, Google News, USPTO, PACER, GitHub, Reddit,
              Secretary of State filings).
            </li>
            <li>
              <strong>Operational telemetry</strong> — request logs (IP, user
              agent, timestamp), error traces, and rate-limit metadata. Used
              solely for service operations and abuse prevention.
            </li>
            <li>
              <strong>Voluntary chat input</strong> — text or audio messages a
              visitor submits to the public homepage chat widget. Audio is
              transcribed to text via Deepgram and discarded; transcripts and
              text messages are retained for service improvement.
            </li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">3. How we use information</h2>
          <ul className="list-disc list-inside space-y-2">
            <li>To operate the Service (authenticate users, match investors to companies, track deal progress).</li>
            <li>To send transactional confirmations to authorized internal staff who text the toll-free number (+1-833-841-8666). Each reply is a 1:1 response to a message that staff member initiated.</li>
            <li>To improve and audit the platform (debugging, capacity planning, model evaluation).</li>
            <li>To comply with legal obligations.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">4. Sharing</h2>
          <p>
            We do not sell personal data. We share data only with:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>Infrastructure subprocessors strictly required to operate the Service: Railway (compute), Netlify (CDN), PostgreSQL hosting, Anthropic (Claude API), OpenAI, Telnyx (SMS), Resend (email), Deepgram (audio transcription).</li>
            <li>Service providers under written confidentiality / data-processing terms.</li>
            <li>Law enforcement when required by valid legal process.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">5. SMS / messaging</h2>
          <p>
            The toll-free number <strong>+1-833-841-8666</strong> is used
            exclusively for internal admin confirmations. Authorized senders
            (manually whitelisted by the system administrator) text investor or
            company info to the number; the system replies once with a
            confirmation containing the created record ID. Reply <strong>STOP</strong>{' '}
            at any time to opt out, <strong>HELP</strong> for help. Message and
            data rates may apply. Opt-in is by manual server-side allowlist
            entry — there is no public subscription flow.
          </p>

          <h2 className="text-xl font-semibold mt-8">6. Data retention</h2>
          <p>
            Account and business records are retained for the life of the
            account plus a reasonable archival period. Operational telemetry is
            retained for 90 days. SMS inbound/outbound metadata is retained for
            12 months. Customers may request deletion via{' '}
            <a href="mailto:hello@getventureos.ai" className="text-apple-blue underline">hello@getventureos.ai</a>.
          </p>

          <h2 className="text-xl font-semibold mt-8">7. Your rights</h2>
          <p>
            You may request access, correction, or deletion of your personal
            data by emailing{' '}
            <a href="mailto:hello@getventureos.ai" className="text-apple-blue underline">hello@getventureos.ai</a>.
            We respond to verified requests within 30 days. Residents of
            California, Colorado, Connecticut, Utah, Virginia, and the EU/UK
            have additional statutory rights including the right to lodge a
            complaint with a supervisory authority.
          </p>

          <h2 className="text-xl font-semibold mt-8">8. Security</h2>
          <p>
            We use TLS for all network traffic, encryption at rest for
            production databases, ed25519 signature verification for inbound
            webhooks, and least-privilege access controls. No system is
            perfectly secure; we report material breaches as required by law.
          </p>

          <h2 className="text-xl font-semibold mt-8">9. Changes</h2>
          <p>
            We may update this policy. Material changes will be announced on
            this page with a new effective date.
          </p>

          <h2 className="text-xl font-semibold mt-8">10. Contact</h2>
          <p>
            Questions? Email{' '}
            <a href="mailto:hello@getventureos.ai" className="text-apple-blue underline">hello@getventureos.ai</a>.
          </p>
        </section>
      </main>

      <footer className="border-t border-apple-divider bg-apple-bg px-6 py-8 text-center text-xs text-apple-subtext">
        &copy; {new Date().getFullYear()} VentureOS LLC.{' '}
        <Link to="/terms" className="hover:text-apple-ink ml-2">Terms</Link>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
